import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import SEO, { SITE_URL } from './SEO';
import Footer from './Footer';
import './PricingPage.css';
import './RateCard.css';
import './WebAppsPage.css';

const ENTERPRISE_ENDPOINT = 'https://audit.ubotika.site/enterprise';

// Systems described by function only — no client names, no client count, and
// nothing that implies these came from different companies.
const BUILDS = [
  {
    tag: 'AI document review',
    title: 'Read a long document, return a structured verdict',
    body:
      'Upload a report, the agent extracts the fields that matter, flags inconsistencies against your rules, and returns a reviewable summary with its reasoning — instead of an analyst reading 40 pages.',
    stack: 'Claude Agent SDK · Node · SSO',
  },
  {
    tag: 'Decision portals',
    title: 'Internal tools your team logs into with Google',
    body:
      'Role-gated portals where a team submits, reviews, and approves work in one place. Google SSO restricted to your domain, so access follows the same rules as your email.',
    stack: 'Next.js · oauth2-proxy · Postgres',
  },
  {
    tag: 'Intake workflows',
    title: 'A public form in front, a private queue behind',
    body:
      'Outside parties submit without an account; your staff work the queue behind SSO. Every submission is validated, logged, routed, and turned into a record — no shared inbox, no lost attachments.',
    stack: 'Node · Supabase · SMTP',
  },
  {
    tag: 'Document generation',
    title: 'Proposals and disclosures, generated not typed',
    body:
      'Structured input in, formatted document out — consistent every time, with the edge cases handled in code rather than in someone\'s memory.',
    stack: 'Node · templating · PDF',
  },
  {
    tag: 'Dashboards',
    title: 'The numbers your team actually asks for',
    body:
      'Operational dashboards wired to live data, behind the same SSO as everything else. Built around the questions your team asks weekly, not a generic BI install.',
    stack: 'Next.js · Postgres',
  },
  {
    tag: 'Voice & telephony',
    title: 'Phone systems that do more than answer',
    body:
      'Real-time voice pipelines: telephony in, an AI model reasoning mid-call, actions taken in your systems before the caller hangs up.',
    stack: 'Twilio · realtime voice · Node',
  },
];

const APPROACH = [
  {
    title: 'We host it, or you do',
    body:
      'Everything above runs on infrastructure we operate — Linux, Nginx, Postgres, Docker, systemd. If you would rather own it, it ships to your cloud instead.',
  },
  {
    title: 'Auth is not an afterthought',
    body:
      'Internal tools sit behind Google SSO scoped to your domain. Public-facing pieces are deliberately separated from the parts your staff use.',
  },
  {
    title: 'Small scope, running quickly',
    body:
      'We start with the one workflow that costs you the most time, put it in production, and expand from there — rather than a six-month build you see at the end.',
  },
  {
    title: 'You get the source',
    body:
      'The code is yours. No per-seat licence on your own internal tool, and no vendor lock if you later bring it in-house.',
  },
];

export const FAQS = [
  {
    q: 'What does a custom web app actually cost?',
    a: 'A single focused internal tool — one workflow, one team, SSO, deployed — typically starts in the low five figures. Multi-tool platforms with document AI, integrations, and dashboards scope higher. We quote a fixed price per phase after a scoping call, so you approve a number before work starts rather than watching an hourly meter.',
  },
  {
    q: 'How is this different from hiring a dev shop?',
    a: 'Most shops build what you specify and hand it over. We run the systems we build — hosting, monitoring, and the fixes — so the thing still works six months later. And because we work in AI systems daily, the document-reading and reasoning parts are the normal case for us rather than a subcontracted add-on.',
  },
  {
    q: 'Can you add AI to software we already have?',
    a: 'Usually, yes. If your existing system has a database or an API we can reach, we can add document review, extraction, routing, or drafting alongside it without replacing what already works.',
  },
  {
    q: 'How long does a first build take?',
    a: 'A single well-scoped internal tool is typically a few weeks from kickoff to your team using it in production. We deliberately keep the first scope small so you are using something real early, then extend it.',
  },
  {
    q: 'Who owns the code and the data?',
    a: 'You do, both. The source is handed over, and your data stays in a database you control. If we host it and you later want to move, it moves with you.',
  },
  {
    q: 'Do you sign an NDA?',
    a: 'Yes, before any scoping conversation that touches your systems or data. We also do not publish client names or screenshots — the work described on this page is deliberately anonymized for exactly that reason.',
  },
];

const schema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Custom Web App & Internal Tool Development — UBOTIKA',
    serviceType: 'Custom web application and AI software development',
    description:
      'UBOTIKA builds custom web applications, AI document-review systems, SSO-gated internal portals, intake workflows, and operational dashboards for Los Angeles businesses.',
    provider: {
      '@type': 'LocalBusiness',
      '@id': `${SITE_URL}/#organization`,
      name: 'UBOTIKA',
      url: SITE_URL,
      telephone: '+1-310-912-3548',
      email: 'hello@ubotika.com',
    },
    areaServed: { '@type': 'City', name: 'Los Angeles' },
    offers: { '@type': 'Offer', availability: 'https://schema.org/InStock', priceCurrency: 'USD' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Web Apps', item: `${SITE_URL}/web-apps` },
    ],
  },
];

function ProjectForm() {
  const [firm, setFirm] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (hp) return;
    if (!firm.trim()) { setErrorMsg('Please enter your company name.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErrorMsg('Please enter a valid work email.'); return; }
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(ENTERPRISE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firm: firm.trim(),
          name: name.trim(),
          email: email.trim(),
          role: 'Web app enquiry',
          note: note.trim(),
          source: 'web-apps',
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Could not send. Please email hello@ubotika.com directly.');
    }
  };

  if (status === 'success') {
    return (
      <div className="wa-form wa-form--done">
        <h3>Got it.</h3>
        <p>
          We&apos;ll reply from hello@ubotika.com within one business day — with questions
          about the workflow, not a sales sequence.
        </p>
      </div>
    );
  }

  return (
    <form className="wa-form" onSubmit={onSubmit} noValidate>
      <h3>Describe the workflow that&apos;s costing you time</h3>
      <p className="wa-form-lede">
        No spec required. Tell us what your team does manually today and we&apos;ll tell
        you honestly whether software fixes it.
      </p>

      <div className="wa-field">
        <label htmlFor="wa-firm">Company</label>
        <input id="wa-firm" type="text" value={firm} onChange={(e) => setFirm(e.target.value)} required />
      </div>
      <div className="wa-field-row">
        <div className="wa-field">
          <label htmlFor="wa-name">Your name</label>
          <input id="wa-name" type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </div>
        <div className="wa-field">
          <label htmlFor="wa-email">Work email</label>
          <input id="wa-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
        </div>
      </div>
      <div className="wa-field">
        <label htmlFor="wa-note">What happens manually today?</label>
        <textarea id="wa-note" rows="4" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      <input
        type="text" name="website_url_confirm" tabIndex="-1" autoComplete="off"
        aria-hidden="true" className="wa-hp" value={hp} onChange={(e) => setHp(e.target.value)}
      />

      {errorMsg && <p className="wa-form-error">{errorMsg}</p>}

      <button type="submit" className="cta-button wa-submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Start the conversation'}
      </button>
      <p className="wa-form-fine">
        NDA before anything technical. Prefer email?{' '}
        <a href="mailto:hello@ubotika.com?subject=Custom%20web%20app">hello@ubotika.com</a>
      </p>
    </form>
  );
}

function WebAppsPage() {
  return (
    <>
      <SEO
        title="Custom Web Apps & AI Internal Tools | Los Angeles | UBOTIKA"
        description="UBOTIKA builds custom web applications for Los Angeles businesses: AI document review, SSO-gated internal portals, intake workflows, and operational dashboards. We build them, run them, and hand you the source."
        path="/web-apps"
        schema={schema}
      />
      <main id="main-content" className="pricing-page ratecard-page wa-page">
        <section className="pricing-hero">
          <h1>The phone is one system. We build the rest.</h1>
          <p className="pricing-sub">
            Most of what we build never rings: AI document review, internal portals your
            team logs into with Google, intake workflows, dashboards, and the automation
            underneath them. Custom software for Los Angeles businesses — built, hosted,
            and maintained by the people who wrote it.
          </p>
        </section>

        <section className="ratecard-section">
          <h2>What we build</h2>
          <p className="ratecard-lede">
            Real systems running in production today, described by what they do. We
            don&apos;t publish client names or screenshots.
          </p>
          <div className="wa-grid">
            {BUILDS.map((b) => (
              <article className="wa-card" key={b.title}>
                <p className="wa-card-tag">{b.tag}</p>
                <h3>{b.title}</h3>
                <p className="wa-card-body">{b.body}</p>
                <p className="wa-card-stack">{b.stack}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-section">
          <h2>How we work</h2>
          <div className="ratecard-grid ratecard-grid--4">
            {APPROACH.map((a) => (
              <article className="ratecard-vertical" key={a.title}>
                <h3>{a.title}</h3>
                <p className="wa-card-body">{a.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-section">
          <h2>Questions, answered straight</h2>
          <div className="pricing-faq-list">
            {FAQS.map((f) => (
              <article className="pricing-faq-item" key={f.q}>
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="ratecard-pilot wa-cta">
          <div>
            <h2>Start with the workflow that hurts most.</h2>
            <p>
              We scope one thing, put it in production, and go from there. If software
              isn&apos;t the answer, we&apos;ll say so — that&apos;s a cheaper conversation
              than a build you don&apos;t need.
            </p>
            <ul className="wa-points">
              <li>Fixed price per phase, approved before work starts</li>
              <li>You own the source and the data</li>
              <li>NDA before anything technical</li>
            </ul>
          </div>
          <ProjectForm />
        </section>

        <section className="ratecard-section">
          <p className="ratecard-fine">
            Looking for the phone side instead? See{' '}
            <Link to="/pricing">AI receptionist plans</Link> or call{' '}
            <a href="tel:+13109123548">(310) 912-3548</a>.
          </p>
        </section>
        <Footer />
      </main>
    </>
  );
}

export default WebAppsPage;
